import { Injectable } from '@angular/core';
import { map, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AppShellLauncherAlignment, AppShellLaunchType, ContextGQL, ContextQuery, ContextsGQL } from '../../members.api.g';
import { ThemeService } from 'src/app/shared/services/theme.service';
import { canAccess } from 'src/app/pages-frontend/utils/utils';
import type { AccessRule } from 'src/app/shared/access/access-rules';
import { MenuHelper } from '../helpers';
import { AppContext } from '../contracts';
import { LauncherTypes } from '../constants';
import { LauncherModel } from '../models';

type ApiLauncher = NonNullable<ContextQuery['context']['byId']['launchers'][number]>;
/** Launcher after mapping: label resolved to its text. */
type MappedLauncher = Omit<ApiLauncher, 'label'> & { label: string };

/**
 * Labels of the existing top-level API entry "Reporting" (the API delivers the
 * label in the user's language). Only used to take over its icon for the client
 * launcher of /admin/statistics/dashboard; can be removed together with getClientLaunchers().
 */
const REPORTING_LAUNCHER_LABELS: readonly string[] = ['Reporting', 'Berichte'];

/**
 * Alias of the reporting folder launcher delivered by the seed. The launcher key is the alias,
 * possibly prefixed with a context-specific part (the code base already treats keys as
 * "<prefix>DASHBOARD_<suffix>", see SecondaryMenuComponent). Hence the match on "equals or ends
 * with <separator>alias" instead of strict equality; never match by label (translated).
 */
const REPORTING_FOLDER_ALIAS: AccessRule = 'STATISTICS_REDESIGN'; // seed launcher alias = access rule name
const REPORTING_FOLDER_KEY = new RegExp(`(^|[_.:/-])${REPORTING_FOLDER_ALIAS}$`);
const isReportingFolderKey = (key: string | null | undefined): boolean => !!key && REPORTING_FOLDER_KEY.test(key);

/** Plain, JSON-serializable copy of a menu node: without the circular `parent` reference. */
function toPlainNavItem(node: LauncherModel): LauncherModel {
  const copy: LauncherModel = { ...node, items: node.items?.length ? node.items.map(toPlainNavItem) : undefined };
  delete copy.parent;
  return copy;
}

@Injectable({
  providedIn: 'root',
})
export class ContextService {
  constructor(
    private context: ContextGQL,
    private contextsGQL: ContextsGQL,
    private themeService: ThemeService,
    private translate: TranslateService
  ) {}

  getContext(id: string): Observable<AppContext> {
    return this.context
      .fetch(
        { id: { id } },
        {
          /*notifyOnNetworkStatusChange: true, useInitialLoading: true*/
        }
      )
      .pipe(
        map(({ data, loading }) => {
          const context = data.context.byId;
          const apiLaunchers: MappedLauncher[] = context.launchers.map((launcher) => ({
            ...launcher,
            label: launcher.label.value,
            image: launcher.image.replace('${theme}', this.themeService.getThemeName()),
          }));
          const launchers: MappedLauncher[] = [...apiLaunchers, ...this.getClientLaunchers(apiLaunchers)];
          const menuLaunchers = launchers.filter((l) => l.type !== AppShellLaunchType.Profile);
          const profileLaunchers = launchers.filter((l) => l.type === AppShellLaunchType.Profile);
          const primaryLaunchers = menuLaunchers.filter(
            (l) => l.alignment === AppShellLauncherAlignment.Primary || !l.alignment
          );
          const secondaryLaunchers = menuLaunchers.filter((l) => l.alignment === AppShellLauncherAlignment.Secondary);
          const tertiaryLaunchers = menuLaunchers.filter((l) => l.alignment === AppShellLauncherAlignment.Tertiary);
          const primaryMenuItems = MenuHelper.createMenu(primaryLaunchers);
          const secondaryMenuItems = MenuHelper.createMenu(secondaryLaunchers);
          const tertiaryMenuItems = MenuHelper.createMenu(tertiaryLaunchers);
          MenuHelper.sortLaunchers(primaryMenuItems);
          MenuHelper.sortLaunchers(secondaryMenuItems);
          const reportingNavItems = this.collapseReportingFolder(primaryMenuItems);
          const menuItems = {
            primary: primaryMenuItems.filter((element) => !(element.type === 'FOLDER' && !element.items)), //removing empty folders
            secondary: secondaryMenuItems.filter((element) => !(element.type === 'FOLDER' && !element.items)),
            tertiary: tertiaryMenuItems.filter((element) => !(element.type === 'FOLDER' && !element.items)),
          };
          return {
            id: context.id,
            key: context.key,
            image: context.image,
            label: { ...context.label },
            menuItems,
            profileItems: profileLaunchers,
            launchers,
            reportingNavItems,
            ownProfileAccess: data.ownProfileAccess,
          };
        })
      );
  }

  /**
   * Turns the reporting folder (key STATISTICS_REDESIGN) of the primary menu into a single route
   * entry: its children are handed over as the return value (for the reporting pages' own
   * sub-navigation) and removed from the tree node, so the admin navigation shows no expander
   * but navigates to the first child's path on click. Mutates the node in place; context.launchers
   * is not touched. Returns [] if the folder is missing or has no children.
   */
  private collapseReportingFolder(primaryMenuItems: LauncherModel[]): LauncherModel[] {
    const folder = primaryMenuItems.find((item) => isReportingFolderKey(item.key));
    if (!folder?.items?.length) {
      return [];
    }
    const navItems = folder.items.map(toPlainNavItem);
    const firstPath = navItems.find((item) => item.path)?.path;
    if (!firstPath) {
      return navItems;
    }
    delete folder.items;
    folder.type = LauncherTypes.ROUTE;
    folder.path = firstPath;
    return navItems;
  }

  /**
   * Launchers added on the client in addition to the ones from the API.
   *
   * TEMPORARY (interim solution): the admin page "Statistics dashboard" (/admin/statistics/dashboard) is not
   * yet configured as a launcher in the API. Until then it is added here for admins
   * as a ROUTE launcher in the primary menu (admin navigation).
   * Only used as a fallback while the seed launcher STATISTICS_REDESIGN (folder "Reports (new)" with
   * ROUTE children) is not registered in a context: as soon as the API delivers it, nothing is added
   * (otherwise duplicate entries / label collision). Only if canAccess(STATISTICS_REDESIGN), like the seed's ShowForRoles.
   * TODO: remove this method and the key redesign.statistics.MENU_LABEL once the seed is registered everywhere.
   *
   * The label must differ from all API launcher labels: MenuHelper.addMenuItem()
   * silently drops entries with a duplicate label (e.g. the existing entry
   * "Reporting"/"Berichte"). Translations are loaded by the APP_INITIALIZER before
   * the context is resolved, so instant() is safe here. Like the API labels, the
   * text does not follow a later language switch.
   *
   * The icon is taken over from the existing API entry "Reporting" (see
   * REPORTING_LAUNCHER_LABELS); without it the menu falls back to the preview text.
   */
  private getClientLaunchers(apiLaunchers: MappedLauncher[]): MappedLauncher[] {
    if (apiLaunchers.some((l) => isReportingFolderKey(l.key)) || !canAccess(REPORTING_FOLDER_ALIAS)) {
      return [];
    }
    const reportingLauncher = apiLaunchers.find((l) => REPORTING_LAUNCHER_LABELS.includes(l.label));
    return [
      {
        id: 'client-route-admin-statistics',
        key: 'admin-statistics',
        uuid: '',
        type: AppShellLaunchType.Route,
        image: reportingLauncher?.image ?? '',
        baseUrl: '',
        path: '/admin/statistics/dashboard',
        alignment: AppShellLauncherAlignment.Primary,
        order: null,
        cssClasses: '',
        additionalConfiguration: '',
        label: this.translate.instant('redesign.statistics.MENU_LABEL'),
      },
    ];
  }

  getContexts() {
    return this.contextsGQL.fetch().pipe(map((data) => data.data.context.onlyWithLaunchers.nodes));
  }
}
